import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './CustomVideoPlayer.css';

interface CustomVideoPlayerProps {
  src: string;
  poster?: string;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  onVideoComplete?: (points?: number) => void;
  videoId?: number;
  initialTime?: number;
  slideLinks?: { title: string; url: string }[];
  imageLinks?: { title: string; url: string }[];
  autoPlay?: boolean;
  submittingTask?: boolean;
}

const CustomVideoPlayer: React.FC<CustomVideoPlayerProps> = ({
  src,
  poster,
  onTimeUpdate,
  onVideoComplete,
  videoId,
  initialTime = 0,
  slideLinks,
  imageLinks,
  autoPlay,
  submittingTask
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(initialTime);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [showCaptionsMenu, setShowCaptionsMenu] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState('auto');
  const [selectedCaption, setSelectedCaption] = useState('off');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [videoError, setVideoError] = useState<string | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasTriggeredComplete = useRef<boolean>(false);

  const playbackRates = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
  const qualities = ['auto', '1080p', '720p', '480p', '360p', '240p'];
  const captions = ['off', 'vi', 'en'];

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    setVideoError(null);

    const updateTime = () => {
      setCurrentTime(video.currentTime);
      setDuration(video.duration);
      if (onTimeUpdate) {
        onTimeUpdate(video.currentTime, video.duration);
      }

      if (video.duration > 0 && video.currentTime >= video.duration - 0.5 && !hasTriggeredComplete.current) {
        hasTriggeredComplete.current = true;
        if (onVideoComplete) {
          onVideoComplete();
        }
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    };

    const handlePause = () => {
      setIsPlaying(false);
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };

    const handleVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };

    const handleError = () => {
      setVideoError(t('course.player.error'));
      setShowControls(true);
    };

    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('volumechange', handleVolumeChange);
    video.addEventListener('error', handleError);

    const handleFullscreenChange = () => {
      const isFullscreenActive = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isFullscreenActive);
      if (isFullscreenActive) {
        setShowControls(true);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const isCurrentlyFullscreen = !!(
          document.fullscreenElement ||
          (document as any).webkitFullscreenElement ||
          (document as any).mozFullScreenElement ||
          (document as any).msFullscreenElement
        );
        if (isCurrentlyFullscreen) {
          const container = containerRef.current;
          if (container) {
            if (document.exitFullscreen) {
              document.exitFullscreen();
            } else if ((document as any).webkitExitFullscreen) {
              (document as any).webkitExitFullscreen();
            } else if ((document as any).mozCancelFullScreen) {
              (document as any).mozCancelFullScreen();
            } else if ((document as any).msExitFullscreen) {
              (document as any).msExitFullscreen();
            }
          }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('volumechange', handleVolumeChange);
      video.removeEventListener('error', handleError);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [onTimeUpdate, isPlaying, t]);

  useEffect(() => {
    hasTriggeredComplete.current = false;
    setVideoError(null);

    const timeoutId = setTimeout(() => {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      const sourceElement = videoElement.querySelector('source') as HTMLSourceElement | null;
      if (sourceElement) {
        const currentSrc = sourceElement.getAttribute('src');
        if (currentSrc !== src) {
          try {
            sourceElement.setAttribute('src', src);
            videoElement.load();
          } catch (error) {
            console.warn('Error updating video source:', error);
          }
        }
      } else {
        try {
          if (videoElement.src !== src) {
            videoElement.src = src;
            videoElement.load();
          }
        } catch (error) {
          console.warn('Error updating video src:', error);
        }
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [src, videoId]);

  useEffect(() => {
    if (initialTime > 0 && videoRef.current) {
      videoRef.current.currentTime = initialTime;
      setCurrentTime(initialTime);
    }
  }, [initialTime, src]); // Update when source or initialTime changes

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
    } else {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {
        });
      }
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const newTime = parseFloat(e.target.value);
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const changePlaybackRate = (rate: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = rate;
    setPlaybackRate(rate);
    setShowSpeedMenu(false);
    setShowSettings(false);
  };

  const changeQuality = (quality: string) => {
    setSelectedQuality(quality);
    setShowQualityMenu(false);
    setShowSettings(false);
  };

  const changeCaption = (caption: string) => {
    setSelectedCaption(caption);
    setShowCaptionsMenu(false);
    setShowSettings(false);
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;

    if (!isFullscreen) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if ((container as any).webkitRequestFullscreen) {
        (container as any).webkitRequestFullscreen();
      } else if ((container as any).mozRequestFullScreen) {
        (container as any).mozRequestFullScreen();
      } else if ((container as any).msRequestFullscreen) {
        (container as any).msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    }
  };

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleContainerMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`custom-video-player-container ${isFullscreen ? 'fullscreen' : ''}`}
      onMouseMove={handleContainerMouseMove}
      onMouseLeave={() => {
        if (isPlaying && !isFullscreen) {
          setShowControls(false);
        }
      }}
    >
      <video
        ref={videoRef}
        className="custom-video-element"
        poster={poster}
        onClick={togglePlay}
        controls={false} // Custom controls
        playsInline
        autoPlay={autoPlay}
        preload="auto"
        crossOrigin="anonymous"
      >
        <source src={src} type="video/mp4" />
        {t('course.player.unsupported')}
      </video>

      {videoError && (
        <div className="custom-video-error">
          {videoError}
        </div>
      )}

      <div className={`custom-video-controls ${showControls ? 'show' : ''}`}>
        <div className="video-progress-container">
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            className="video-progress-bar"
          />
        </div>

        <div className="video-controls-bottom">
          <div className="video-controls-left">
            <button className="video-control-btn" onClick={togglePlay}>
              {isPlaying ? (
                <span className="video-icon">⏸</span>
              ) : (
                <span className="video-icon">▶</span>
              )}
            </button>

            <div className="video-time-display">
              <span>{formatTime(currentTime)}</span>
              <span> / </span>
              <span>{formatTime(duration)}</span>
            </div>

            <button className="video-control-btn" onClick={toggleMute}>
              {isMuted || volume === 0 ? (
                <span className="video-icon">🔇</span>
              ) : volume < 0.5 ? (
                <span className="video-icon">🔉</span>
              ) : (
                <span className="video-icon">🔊</span>
              )}
            </button>

            <div className="volume-control">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                className="volume-slider"
              />
            </div>
          </div>

          <div className="video-controls-right">
            <div className="video-control-menu">
              <button
                className="video-control-btn"
                onClick={() => {
                  setShowCaptionsMenu(!showCaptionsMenu);
                  setShowSpeedMenu(false);
                  setShowQualityMenu(false);
                }}
              >
                <span className="video-icon">CC</span>
              </button>
              {showCaptionsMenu && (
                <div className="video-menu-dropdown">
                  <div className="video-menu-title">{t('course.player.subtitles')}</div>
                  {captions.map((caption) => (
                    <button
                      key={caption}
                      className={`video-menu-item ${selectedCaption === caption ? 'active' : ''}`}
                      onClick={() => changeCaption(caption)}
                    >
                      {caption === 'off' ? t('course.player.off') : caption === 'vi' ? t('course.player.vietnamese') : t('course.player.english')}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="video-control-menu">
              <button
                className="video-control-btn"
                onClick={() => {
                  setShowSettings(!showSettings);
                  setShowSpeedMenu(false);
                  setShowQualityMenu(false);
                  setShowCaptionsMenu(false);
                }}
              >
                <span className="video-icon">⚙️</span>
              </button>
              {showSettings && (
                <div className="video-menu-dropdown">
                  <div className="video-menu-title">{t('course.player.settings')}</div>

                  <div className="video-menu-section">
                    <button
                      className="video-menu-item"
                      onClick={() => {
                        setShowSpeedMenu(!showSpeedMenu);
                        setShowQualityMenu(false);
                      }}
                    >
                      {t('course.player.speed')}
                      <span className="video-menu-value">{playbackRate}x</span>
                    </button>
                    {showSpeedMenu && (
                      <div className="video-submenu">
                        {playbackRates.map((rate) => (
                          <button
                            key={rate}
                            className={`video-menu-item ${playbackRate === rate ? 'active' : ''}`}
                            onClick={() => changePlaybackRate(rate)}
                          >
                            {rate}x
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="video-menu-section">
                    <button
                      className="video-menu-item"
                      onClick={() => {
                        setShowQualityMenu(!showQualityMenu);
                        setShowSpeedMenu(false);
                      }}
                    >
                      {t('course.player.quality')}
                      <span className="video-menu-value">
                        {selectedQuality === 'auto' ? t('course.player.auto') : selectedQuality}
                      </span>
                    </button>
                    {showQualityMenu && (
                      <div className="video-submenu">
                        {qualities.map((quality) => (
                          <button
                            key={quality}
                            className={`video-menu-item ${selectedQuality === quality ? 'active' : ''}`}
                            onClick={() => changeQuality(quality)}
                          >
                            {quality === 'auto' ? t('course.player.auto') : quality}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <button className="video-control-btn" onClick={toggleFullscreen} title={isFullscreen ? t('course.player.exitFullscreen') : t('course.player.fullscreen')}>
              <span className="video-icon">
                {isFullscreen ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                  </svg>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomVideoPlayer;
